import { RouterLink } from '@angular/router';
import { NgFor, NgIf, NgStyle } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';

/**
 * interfaces
 */
import { IAdvertisementResponse } from 'src/app/core/interfaces/ads/advertisement';

/**
 * enums
 */
import { CATEGOR_VALUE_TYPE } from 'src/app/core/enums/category/cate-value-types';

/**
 * Directives
*/
import { ImgUrlCheckDirective } from 'src/app/core/directives/img/img-url-check.directive';
import { PreventDefaultDirective } from 'src/app/core/directives/dom/prevent-default.directive';
import { StopPropagationDirective } from 'src/app/core/directives/dom/stop-propagation.directive';

/**
 * components
 */
import { ChatIconComponent } from 'src/app/shared.standalone/icons/svg/chat-icon/chat-icon.component';
import { VirtualIconComponent } from 'src/app/shared.standalone/icons/svg/virtual-icon/virtual-icon.component';

/**
 * pipes
*/
import { PriceHandlerPipe } from "src/app/core/pipes/enmus/price-handler";
import { SizeHandlerPipe } from 'src/app/core/pipes/enmus/size-handler.pipe';

/**
 * services
 */
import { AuthService } from 'src/app/core/services/user/auth.service';
import { AdOperationService } from 'src/app/core/services/ad/ad-operations/ad-operations.service';

@Component({
  standalone: true,
  selector: 'app-advertisement-card',
  templateUrl: './advertisement-card.component.html',
  styleUrls: ['./advertisement-card.component.scss'],
  imports: [
    /**
     * Angular
     */
    NgIf,
    NgFor,
    NgStyle,
    RouterLink,
    /**
     * 
     */
    ChatIconComponent,
    VirtualIconComponent,
    /**
     * Directives
     */
    ImgUrlCheckDirective,
    PreventDefaultDirective,
    StopPropagationDirective,
    /**
     * pipes
     */
    SizeHandlerPipe,
    PriceHandlerPipe,
  ],
})
export class AdvertisementCardComponent implements OnInit, AfterViewInit {

  // Inputs
  /**
   * Advertisement DAta
   */
  @Input({ alias: 'info', required: true }) public Data: IAdvertisementResponse = null;

  /**
   * ViewState
   * @description to submit that Advertisement is view from user
   */
  @Input({ alias: 'submitView' }) public SubmitView: boolean = null;

  /**
   * ViewChild
   */
  @ViewChildren('ProgressChar') private _progressChars!: QueryList<ElementRef<HTMLSpanElement>>;
  @ViewChild('ProgressBar', { read: ElementRef<HTMLDivElement> }) private _progressBar: ElementRef<HTMLDivElement>
  @ViewChild('ProgressContainer', { read: ElementRef<HTMLDivElement> }) private _progressContainer: ElementRef<HTMLDivElement>


  /**
   * fields
   */
  private static NullableValueReturn: string = '----';


  // Accessors
  /**
   * CardOwner
   * @description check if owner ad is current user auth
   */
  public get CardOwner(): boolean {

    // ES6 Destruction
    const { owner: { id: OwnerId } } = this.Data;

    /**
     * 
     */
    return this.Data.owner.id === this._auth.userInfo.value?.id

  }


  /**
   * Card Image
   * @description Advertisement has a lot of images, then return first image in list
   */
  public get CardImage(): string | null {

    /**
     * 
     */
    return this.Data.imgs != null && this.Data.imgs.length > 0 ? this.Data.imgs.at(0): null;

  }


  /**
   * Card Image
   */
  public get CardTitle(): string {

    /**
     * 
     */
    if (this.Data.title != null) {

      // ES6 Destruction
      const { title } = this.Data;

      /**
       * remove char ( - ) & char ( _ )
       */
      const formate = title.replace(/[\s-_]+/g, ' ').trim().split(' ').slice(0, 2).join(' ');

      /**
       * 
       */
      return formate

    }

    return AdvertisementCardComponent.NullableValueReturn;

  }

  /**
   * @description that title depend on sub category Name
   */
  public get CardSubCategoryTitle(): string {

    /**
     * 
     */
    return this.Data.subCategory != null ? this.Data.subCategory.categoryName : 'بدون نوع';

  } 
  
  /**
   * CardLocationZone
   * @description concatenation areaName with cityName in one string
   */
  public get CardLocationZone(): string | null {

    // init
    let formate: string = "";

    /**
     * formatting area
     */
    if (this.Data.area != null) {

      /**
       * remove space
       */
      formate = this.Data.area.areaName.split(' ').slice(0, 2).join(' ').trim();

      /**
       * handler long string
       */
      if (formate.length > 15) formate = formate.slice(0, 15).trim();


    }    

    /**
     * formatting city
     */
    if (this.Data.city != null) {
      
      /**
       * remove space
       */
      let split = this.Data.city.cityName.split(' ').slice(0, 1).join(' ').trim();

      /**
       *  handler long string
       */
      if (split.length > 15) split = split.slice(0, 15).trim();

      /**
       * handler char ( - ) between area & city
       */
      if (this.Data.area != null) formate += ' - ' + split;
      else formate = split
      
    }


    /**
     * 
     */
    return formate || AdvertisementCardComponent.NullableValueReturn

  }


  /**
   * formate size
   */
  public get CardSize(): string {
    
    /**
     * 
     */
    if (this.Data.size != null) return this._adService.handlerSize(this.Data.size)

    /**
     * 
     */
    return AdvertisementCardComponent.NullableValueReturn

  }

  /**
   * achievementRatio
   */
  public get CardProgress(): number | null {
    return this.Data.achievementRatio
  }

  /**
   * CardBedrooms
   * @description loop in properties then check at redfKey then return value
   */
  public get CardBedrooms(): number | string | null {

    const { properties } = this.Data;

    if (Array.isArray(properties) && properties.length > 0) {

      // ES6 Destruction
      const { value } = properties.find(prop => prop.redfKey === 'bedrooms') || {}

      /**
       * 
       */
      return value

    }

    /**
     * 
     */
    return null

  }

  /**
   * link3D
   */
  public get Card3DModel(): string | null {
    

    if (Array.isArray(this.Data.link3D) && this.Data.link3D.length > 0) return this.Data.link3D[0]

    /**
     * 
     */
    return null

  }
  
  /**
   * CategoryType
   */
  public get CategoryType(): CATEGOR_VALUE_TYPE {
    return this.Data.category?.type
  }

  /**
   * IsHasBundledAds
   */
  public get IsHasBundledAds() {
    return this._adService.IsHasBundledAds(this.CategoryType)
  }

  /**
   * IsOffPlanSale
   */
  public get IsOffPlanSale() {
    return this._adService.isOffPlanSale(this.CategoryType)
  }  

  /**
   * BundledAdvertisementUnitsTitle
   */
  public get BundledAdvertisementUnitsTitle(): { total: string, available: string } {

    switch(this.CategoryType) {
      case CATEGOR_VALUE_TYPE.BL:
      case CATEGOR_VALUE_TYPE.OFF_PLAN:
        return { total: 'عدد الوحدات', available: 'المتاحة' };
      case CATEGOR_VALUE_TYPE.PL:
        return { total: 'عدد الأراضي', available: 'المتاحة' };
      case CATEGOR_VALUE_TYPE.VL:
        return { total: 'عدد الفلل', available: 'المتاحة' };      
      default:
        return { total: 'العدد الاجمالي', available: 'المتاح' };
    }

  }
  
  /**
   * 
   */
  public get SplitTexts(): string[] {
    return ('نسبة الانجاز ' + this.CardProgress +'%').split('')
  }

  
  /**
   *
   */
  constructor(
    private _auth: AuthService,
    private readonly _adService: AdOperationService
  ) {}

  // Methods
  /**
   * 
   */
  ngOnInit(): void {}
  
  /**
   * 
  */
  ngAfterViewInit(): void {

    /**
     * _progressChars
     */
    if(this._progressChars.length > 0 && this.IsOffPlanSale) {

      /**
       * 
      */
      setTimeout(() => {

        // init
        const { clientWidth: progressBarWidth } = this._progressBar.nativeElement
        const { clientWidth: progressContainerWidth } = this._progressContainer.nativeElement

        /**
        * 
        */
        for (let index = 0; index < this._progressChars.length; index++) {

          // init
          const span = this._progressChars.get(index);

          /**
          * 
          */
          if (progressBarWidth >= (progressContainerWidth - span.nativeElement.offsetLeft)) {

            if (!span.nativeElement.classList.contains('float-end')) span.nativeElement.classList.add('text-white');
            else span.nativeElement.classList.add('main_color_4th');

          }
          else break;

        }


      }, 1)

    }

  }

  /**
   * HandlerSendContactRequest
   */
  public HandlerSendContactRequest(): void {
    this._adService
      .HandlerSendContactRequest(this.Data)
      ?.subscribe()
  }


}
